// =============================================================================
// ProviderStoreAccessTab — allowlist of stores that may send SELF to this
// provider. Platform admins edit; provider-scoped users read only.
// =============================================================================

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';

interface StoreRow {
  connectionId: string;
  name: string;
  shopDomain: string | null;
  organizationName: string;
}

interface StoresResponse {
  data: {
    selected: StoreRow[];
    available: StoreRow[];
  };
}

export function ProviderStoreAccessTab({
  providerId,
  canEdit,
}: {
  providerId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [draftIds, setDraftIds] = useState<string[] | null>(null);

  const storesQuery = useQuery<StoresResponse, Error>({
    queryKey: ['admin', 'provider', providerId, 'self-claim-stores'],
    queryFn: () =>
      api.get<StoresResponse>(
        `/api/v1/admin/providers/${providerId}/self-claim-stores`,
      ),
  });

  const selectedIds = useMemo(() => {
    if (draftIds) return new Set(draftIds);
    return new Set(
      (storesQuery.data?.data.selected ?? []).map((s) => s.connectionId),
    );
  }, [draftIds, storesQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = storesQuery.data?.data.available ?? [];
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.shopDomain ?? '').toLowerCase().includes(q) ||
        r.organizationName.toLowerCase().includes(q),
    );
  }, [search, storesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (connectionIds: string[]) =>
      api.put<StoresResponse>(
        `/api/v1/admin/providers/${providerId}/self-claim-stores`,
        { connectionIds },
      ),
    onSuccess: () => {
      setDraftIds(null);
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'provider', providerId, 'self-claim-stores'],
      });
    },
  });

  const toggle = (connectionId: string) => {
    if (!canEdit) return;
    const next = new Set(selectedIds);
    if (next.has(connectionId)) next.delete(connectionId);
    else next.add(connectionId);
    setDraftIds([...next]);
  };

  if (storesQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="md" label="Loading stores..." />
      </div>
    );
  }

  if (storesQuery.isError || !storesQuery.data) {
    return (
      <p className="text-sm text-danger">
        {storesQuery.error?.message ?? 'Failed to load store access.'}
      </p>
    );
  }

  const dirty = draftIds != null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        Self-fulfilled orders from these stores can be seen and claimed by
        this provider. The merchant is not billed. Platform admins choose the
        list; the provider cannot add stores.
      </p>

      <Input
        label="Search stores"
        placeholder="Name, domain, or organization"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="max-h-96 overflow-y-auto rounded-md border border-border divide-y border-border">
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-text-tertiary">
            No active Shopify stores match.
          </p>
        ) : (
          filtered.map((row) => (
            <label
              key={row.connectionId}
              className="flex items-start gap-3 px-4 py-3 text-sm"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={selectedIds.has(row.connectionId)}
                disabled={!canEdit}
                onChange={() => toggle(row.connectionId)}
              />
              <span>
                <span className="font-medium text-text-primary">{row.name}</span>
                {row.shopDomain && (
                  <span className="ml-2 text-text-tertiary">{row.shopDomain}</span>
                )}
                <span className="block text-[11px] text-text-tertiary">
                  {row.organizationName}
                </span>
              </span>
            </label>
          ))
        )}
      </div>

      {canEdit && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-text-tertiary">
            {selectedIds.size} store{selectedIds.size === 1 ? '' : 's'} selected
          </p>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || saveMutation.isPending}
            loading={saveMutation.isPending}
            onClick={() => saveMutation.mutate([...selectedIds])}
          >
            Save store access
          </Button>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-sm text-danger">
          {saveMutation.error instanceof Error
            ? saveMutation.error.message
            : 'Failed to save store access.'}
        </p>
      )}
    </div>
  );
}
